/**
 * 시각 표시 공용 유틸 (FO datetime SSoT) — contracts/datetime-standard.md.
 *
 * 백엔드 응답 시각 필드는 ISO-8601 + offset(예: `2026-05-29T01:12:00+00:00`)으로 내려온다.
 * KST 변환·표시 포맷은 presentation layer(여기)에서만 수행한다(Service/Repository 변환 금지 — 헌법 VI).
 * 화면은 raw 문자열을 직접 출력하지 않고 본 유틸을 경유한다 — 포맷 문자열 컴포넌트 중복 금지.
 * dayjs 직접 import·Date 로캘 포맷은 ESLint 가 이 파일 밖에서 막는다(eslint.config.js `DATETIME_RULE`).
 */
import dayjs, { type Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

export const KST = 'Asia/Seoul';

/** ISO-8601 오프셋 접미사(Z, +09:00, +0900) — 있으면 절대시각, 없으면 KST 벽시계로 해석한다. */
const OFFSET_SUFFIX = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * 값을 KST 기준 dayjs 로 해석한다(표시와 일수 산술이 같은 규칙을 공유).
 * 오프셋이 있으면 절대시각을 KST 로 변환하고, 날짜만 있는 값(`LocalDate`)은 KST 벽시계로 본다 —
 * 브라우저 로컬 타임존으로 해석하면 비-KST 환경에서 서버의 KST 판정과 하루 어긋난다.
 */
export function toKstDayjs(value: string): Dayjs {
  return OFFSET_SUFFIX.test(value) ? dayjs(value).tz(KST) : dayjs.tz(value, KST);
}

/** 지금(KST). */
export function kstNow(): Dayjs {
  return dayjs().tz(KST);
}

/** 오늘(KST) `YYYY-MM-DD` — `<input type="date">` 의 max 등 입력 값 형식. */
export function kstTodayIso(): string {
  return kstNow().format('YYYY-MM-DD');
}

/** 오늘(KST)에서 N년 더하거나 뺀 날짜(YYYY-MM-DD) — 날짜 입력의 min·max 범위용. */
export function kstTodayShiftYearsIso(years: number): string {
  return kstNow().add(years, 'year').format('YYYY-MM-DD');
}

/** 날짜 → KST `M월 D일`(캘린더 시점 본문). 빈 값은 빈 문자열. */
export function formatKstMonthDay(value: string | null | undefined): string {
  return value ? toKstDayjs(value).format('M월 D일') : '';
}

/** 날짜 → KST `M/D`(좁은 칸·오늘 표기). 빈 값은 빈 문자열. */
export function formatKstShortDate(value: string | null | undefined): string {
  return value ? toKstDayjs(value).format('M/D') : '';
}

/** 두 날짜 사이 KST 달력일 수(양끝 포함). 파싱 불가면 null. */
export function kstInclusiveDays(from: string, to: string): number | null {
  const start = toKstDayjs(from);
  const end = toKstDayjs(to);
  if (!start.isValid() || !end.isValid()) return null;
  return end.startOf('day').diff(start.startOf('day'), 'day') + 1;
}

/** 시각 → KST `YYYY.MM.DD HH:mm`. null/undefined·빈 값은 빈 문자열. */
export function formatKst(value: string | null | undefined): string {
  return value ? toKstDayjs(value).format('YYYY.MM.DD HH:mm') : '';
}

/**
 * 날짜 표시 단일 함수 → KST `YYYY.MM.DD`. 오프셋 포함 시각과 날짜만 있는 값(`LocalDate`) 모두 받는다 —
 * 해석은 `toKstDayjs` 한 규칙이라 브라우저 타임존과 무관하게 같은 달력일이 나온다. null/undefined·빈 값은 빈 문자열.
 */
export function formatKstDate(value: string | null | undefined): string {
  return value ? toKstDayjs(value).format('YYYY.MM.DD') : '';
}

/** 시각 → KST `HH:mm`. null/undefined·빈 값은 빈 문자열. */
export function formatKstTime(value: string | null | undefined): string {
  return value ? toKstDayjs(value).format('HH:mm') : '';
}

/** 시각·날짜가 오늘(KST 달력일)인지. 빈 값은 false. */
export function isKstToday(value: string | null | undefined): boolean {
  return value ? toKstDayjs(value).isSame(kstNow(), 'day') : false;
}
